import React, { useState } from "react";
import { Icon } from "./Icons";
import "./PaymentStep.css";

/**
 * Stripe-style payment mockup.
 *
 * Looks and feels like real Stripe Checkout with:
 *   - Card number formatting (groups of 4)
 *   - Auto-detected card brand
 *   - Expiry MM/YY formatting
 *   - CVC + ZIP fields
 *   - "Processing..." state with realistic timing
 *   - Success handoff
 *
 * NO real charge happens. To make this real:
 *   1. npm install @stripe/stripe-js @stripe/react-stripe-js
 *   2. Replace the form with <CardElement> from @stripe/react-stripe-js
 *   3. Create a PaymentIntent on your server
 *   4. Confirm with stripe.confirmCardPayment(clientSecret)
 *
 * This mockup intentionally mimics Stripe's UX so a real swap is
 * minimally disruptive to the surrounding flow.
 */

const detectCardBrand = (number) => {
    const n = number.replace(/\s/g, "");
    if (/^4/.test(n)) return "visa";
    if (/^5[1-5]/.test(n) || /^2[2-7]/.test(n)) return "mastercard";
    if (/^3[47]/.test(n)) return "amex";
    if (/^6(?:011|5)/.test(n)) return "discover";
    return "card";
};

const formatCardNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
};

const formatExpiry = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    if (digits.length < 3) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

function PaymentStep({ bookingData, therapist, onComplete }) {
    const [cardNumber, setCardNumber] = useState("");
    const [expiry, setExpiry] = useState("");
    const [cvc, setCvc] = useState("");
    const [zip, setZip] = useState("");
    const [name, setName] = useState(
        `${bookingData.client.firstName} ${bookingData.client.lastName}`.trim()
    );
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState("");

    const brand = detectCardBrand(cardNumber);
    const cardDigits = cardNumber.replace(/\s/g, "");

    const isValid =
        cardDigits.length >= 13 &&
        expiry.length === 5 &&
        cvc.length >= 3 &&
        zip.length >= 5 &&
        name.trim().length > 1;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid || processing) return;

        setError("");
        setProcessing(true);

        // Simulate realistic processing delay
        await new Promise((resolve) => setTimeout(resolve, 1800));

        // 5% chance of simulated decline so the demo can show error states
        if (cardDigits.endsWith("0002")) {
            setError("Your card was declined. Please try another card.");
            setProcessing(false);
            return;
        }

        // In a real integration, await stripe.confirmCardPayment() here
        onComplete({
            last4: cardDigits.slice(-4),
            brand,
            cardholderName: name,
        });
    };

    const amountDollars = (bookingData.service.priceCents / 100).toFixed(2);

    return (
        <div className="payment-container">
            <div className="payment-summary">
                <h3>Order summary</h3>
                <div className="payment-summary-rows">
                    {therapist && (
                        <div className="payment-summary-row">
                            <span className="payment-summary-label">Therapist</span>
                            <span className="payment-summary-value">
                                {therapist.name}, {therapist.credential}
                            </span>
                        </div>
                    )}
                    <div className="payment-summary-row">
                        <span className="payment-summary-label">Service</span>
                        <span className="payment-summary-value">
                            {bookingData.service.name}
                        </span>
                    </div>
                    <div className="payment-summary-row">
                        <span className="payment-summary-label">Date &amp; time</span>
                        <span className="payment-summary-value">
                            {bookingData.date.toLocaleDateString("en-US", {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                            })}{" "}
                            · {bookingData.time}
                        </span>
                    </div>
                    <div className="payment-summary-row">
                        <span className="payment-summary-label">Duration</span>
                        <span className="payment-summary-value">
                            {bookingData.service.duration}
                        </span>
                    </div>
                </div>
                <div className="payment-summary-total">
                    <span>Total due</span>
                    <span className="payment-summary-amount">${amountDollars}</span>
                </div>
                <div className="payment-summary-note">
                    <Icon name="lock" size={14} />
                    <span>
                        Card held now. Charged in full at the time of session per the
                        24-hour cancellation policy.
                    </span>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="payment-form">
                <h3>Payment details</h3>

                <div className="payment-disclaimer">
                    <Icon name="lock" size={16} />
                    <p>
                        <strong>Demo mode:</strong> This is a Stripe-style payment mockup
                        — no card data is transmitted or stored. Try{" "}
                        <code>4242 4242 4242 4242</code> for success or{" "}
                        <code>4000 0000 0000 0002</code> for a declined-card demo.
                    </p>
                </div>

                <div className="payment-field">
                    <label>Cardholder name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Name on card"
                        autoComplete="cc-name"
                        required
                    />
                </div>

                <div className="payment-field">
                    <label>Card number</label>
                    <div className="payment-card-input">
                        <input
                            type="text"
                            value={cardNumber}
                            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                            placeholder="1234 1234 1234 1234"
                            autoComplete="cc-number"
                            inputMode="numeric"
                            required
                        />
                        <span className={`card-brand card-brand-${brand}`}>
                            {brand === "visa" && "VISA"}
                            {brand === "mastercard" && "MC"}
                            {brand === "amex" && "AMEX"}
                            {brand === "discover" && "DISC"}
                            {brand === "card" && (
                                <Icon name="briefcase" size={20} />
                            )}
                        </span>
                    </div>
                </div>

                <div className="payment-field-row">
                    <div className="payment-field">
                        <label>Expiry</label>
                        <input
                            type="text"
                            value={expiry}
                            onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                            placeholder="MM/YY"
                            autoComplete="cc-exp"
                            inputMode="numeric"
                            required
                        />
                    </div>
                    <div className="payment-field">
                        <label>CVC</label>
                        <input
                            type="text"
                            value={cvc}
                            onChange={(e) => setCvc(e.target.value.replace(/\D/g, "").slice(0, 4))}
                            placeholder="CVC"
                            autoComplete="cc-csc"
                            inputMode="numeric"
                            required
                        />
                    </div>
                    <div className="payment-field">
                        <label>ZIP</label>
                        <input
                            type="text"
                            value={zip}
                            onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 10))}
                            placeholder="ZIP"
                            autoComplete="postal-code"
                            inputMode="numeric"
                            required
                        />
                    </div>
                </div>

                {error && (
                    <div className="payment-error">
                        <Icon name="lock" size={16} />
                        <span>{error}</span>
                    </div>
                )}

                <button
                    type="submit"
                    className="btn-primary btn-large payment-submit"
                    disabled={!isValid || processing}
                >
                    {processing ? (
                        <span className="payment-processing">
                            <span className="payment-spinner" />
                            Processing...
                        </span>
                    ) : (
                        <>Confirm and pay ${amountDollars}</>
                    )}
                </button>

                <p className="payment-fineprint">
                    Powered by Stripe · Your payment info is encrypted and never stored
                    on our servers.
                </p>
            </form>
        </div>
    );
}

export default PaymentStep;